import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../api';
import { useAuth } from '../AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import './ProjectBackingSidebar.css';

const CHAIN_NAMES = {
  btc: 'Bitcoin (BTC)',
  eth: 'Ethereum (ETH)',
  usdt_bep20: 'USDT BEP20 (BSC)',
  usdt_trc20: 'USDT TRC20 (TRON)',
};

function ProjectBackingSidebar({ projectId, project, onPaymentConfirmed, openBuyTrigger }) {
  const { user } = useAuth();
  const [isBacking, setIsBacking] = useState(false);
  const [userBacking, setUserBacking] = useState(null);
  const [backerCount, setBackerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [tier, setTier] = useState('supporter');
  const [isPublic, setIsPublic] = useState(true);

  const pricingType = project?.pricing_type || 'free';
  const price = project?.price;
  const currency = project?.currency || 'USD';
  const isPaidProduct = pricingType !== 'free';

  // Invoice / payment state
  const [selectedChain, setSelectedChain] = useState('btc');
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [pollInterval, setPollInterval] = useState(null);
  const [offerAmount, setOfferAmount] = useState(price || '');

  // Get available wallets for this project
  const [wallets, setWallets] = useState([]);

  useEffect(() => {
    loadBackingStatus();
    if (isPaidProduct) {
      loadWallets();
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user]);

  // Allow parent to trigger opening the buy modal
  useEffect(() => {
    if (openBuyTrigger) {
      openBackModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBuyTrigger]);

  const loadWallets = async () => {
    try {
      const response = await projectsAPI.getWallets(projectId);
      if (response.data.success) {
        setWallets(response.data.wallets || []);
      }
    } catch (e) {
      console.log('Could not load wallets');
    }
  };

  const loadBackingStatus = async () => {
    try {
      const response = await projectsAPI.getBackers(projectId);
      if (response.data.success) {
        setBackerCount(response.data.count);
        setIsBacking(response.data.is_backing || false);
        setUserBacking(response.data.user_backing || null);
      }
    } catch (error) {
      console.error('Error loading backing status:', error);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (invId) => {
    if (pollInterval) clearInterval(pollInterval);
    const interval = setInterval(async () => {
      try {
        const resp = await projectsAPI.checkInvoiceStatus(projectId, invId);
        if (resp.data.success && resp.data.invoice.status === 'paid') {
          clearInterval(interval);
          setPollInterval(null);
          setInvoice(resp.data.invoice);
          loadBackingStatus();
          if (onPaymentConfirmed) onPaymentConfirmed();
          alert('Payment confirmed! You can now download.');
        }
      } catch (e) {
        console.log('Poll error');
      }
    }, 10000);
    setPollInterval(interval);
  };

  const handleCreateInvoice = async () => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    setPaymentError('');
    setInvoiceLoading(true);
    try {
      const data = {
        chain: selectedChain,
        expected_amount: pricingType === 'fixed' ? parseFloat(price) : (parseFloat(offerAmount) || undefined),
        expected_currency: currency,
      };
      const response = await projectsAPI.createInvoice(projectId, data);
      if (response.data.success) {
        setInvoice(response.data.invoice);
        if (response.data.invoice.status === 'pending') {
          startPolling(response.data.invoice.id);
        }
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setPaymentError(error.response?.data?.error || 'Failed to create invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleBackProject = async () => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    try {
      const data = {
        message: message.trim(),
        tier: tier,
        is_public: isPublic
      };
      const response = await projectsAPI.backProject(projectId, data);
      if (response.data.success) {
        setShowModal(false);
        setMessage('');
        loadBackingStatus();
      }
    } catch (error) {
      console.error('Error backing project:', error);
      alert('Failed to back project. Please try again.');
    }
  };

  const handleUnback = async () => {
    if (!userBacking) return;
    if (!window.confirm('Are you sure you want to remove your support?')) return;
    try {
      const response = await projectsAPI.unbackProject(projectId, userBacking.id);
      if (response.data.success) {
        loadBackingStatus();
      }
    } catch (error) {
      console.error('Error removing support:', error);
      alert('Failed to remove support. Please try again.');
    }
  };

  const openBackModal = () => {
    setMessage(userBacking?.message || '');
    setTier(userBacking?.tier || 'supporter');
    setIsPublic(userBacking?.is_public !== false);
    setPaymentError('');
    setInvoice(null);
    setOfferAmount(price || '');
    if (wallets.length > 0) {
      setSelectedChain(wallets[0].chain);
    }
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="card sidebar-card">
        <div className="sidebar-loading">Loading...</div>
      </div>
    );
  }

  // For paid products, show buy UI with invoice system
  if (isPaidProduct) {
    const isPaid = invoice?.status === 'paid' || userBacking?.payment_status === 'paid';
    const isPending = invoice?.status === 'pending' || userBacking?.payment_status === 'pending';
    const availableChains = wallets.map(w => w.chain);

    return (
      <>
        <div className="card sidebar-card backing-sidebar">
          <h3 className="sidebar-title">🛒 Buy This Product</h3>

          <div className="backing-stats">
            <div className="backer-count-display">
              <span className="backer-count-number">
                {pricingType === 'pwyw' ? 'Any' : `${price} ${currency}`}
              </span>
              <span className="backer-count-label">
                {pricingType === 'pwyw' ? 'Pay What You Want' : 'Price'}
              </span>
            </div>
          </div>

          {user ? (
            <div className="backing-actions-sidebar">
              {isPaid ? (
                <button className="btn btn-backing-sidebar" disabled>
                  ✓ Paid — Download Unlocked
                </button>
              ) : isPending ? (
                <button className="btn btn-warning-sidebar" disabled>
                  ⏳ Awaiting Payment...
                </button>
              ) : (
                <button onClick={openBackModal} className="btn btn-primary-sidebar">
                  🛒 Buy Now
                </button>
              )}
            </div>
          ) : (
            <a href="/login" className="btn btn-primary-sidebar">
              Login to Buy
            </a>
          )}

          {availableChains.length > 0 && (
            <p className="backing-help-text">
              Accepts: {availableChains.map(c => CHAIN_NAMES[c] || c).join(', ')}
            </p>
          )}
          {availableChains.length === 0 && (
            <p className="backing-help-text" style={{color: 'var(--accent-error, #c96a6a)'}}>
              Seller has not set up wallets yet.
            </p>
          )}
        </div>

        {/* Payment Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Buy This Product</h3>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>

              <div className="modal-body">
                {invoice?.status === 'paid' ? (
                  <div className="alert alert-success">
                    ✅ Payment confirmed! You can now download this product.
                  </div>
                ) : invoice?.status === 'pending' ? (
                  <>
                    <div className="alert alert-warning">
                      ⏳ Send payment to the address below. We'll detect it automatically.
                    </div>
                    <div className="form-group" style={{textAlign: 'center'}}>
                      <QRCodeSVG value={invoice.address} size={180} bgColor="#ffffff" fgColor="#000000" />
                    </div>
                    <div className="form-group">
                      <label>Deposit Address</label>
                      <div className="crypto-address-box">
                        <code style={{ wordBreak: 'break-all', display: 'block', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                          {invoice.address}
                        </code>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary mt-2"
                          onClick={() => {
                            navigator.clipboard.writeText(invoice.address);
                            alert('Address copied!');
                          }}
                        >
                          📋 Copy Address
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Amount to Send</label>
                      <div className="form-control-static">
                        <strong>{invoice.expected_amount || 'Any'} {invoice.expected_currency}</strong>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Chain</label>
                      <div className="form-control-static">
                        {CHAIN_NAMES[invoice.chain] || invoice.chain}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {pricingType === 'fixed' && (
                      <div className="form-group">
                        <label>Price</label>
                        <div className="form-control-static">
                          <strong>{price} {currency}</strong>
                        </div>
                      </div>
                    )}

                    {pricingType === 'pwyw' && (
                      <div className="form-group">
                        <label>Your Offer ({currency})</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={offerAmount}
                          onChange={(e) => setOfferAmount(e.target.value)}
                          placeholder="Enter amount"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Pay With</label>
                      <select
                        className="form-control"
                        value={selectedChain}
                        onChange={(e) => setSelectedChain(e.target.value)}
                      >
                        {availableChains.length === 0 && (
                          <option value="">No wallets available</option>
                        )}
                        {availableChains.map((chain) => (
                          <option key={chain} value={chain}>{CHAIN_NAMES[chain] || chain}</option>
                        ))}
                      </select>
                    </div>

                    {paymentError && (
                      <div className="alert alert-error">{paymentError}</div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                {!invoice && availableChains.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateInvoice}
                    disabled={invoiceLoading}
                  >
                    {invoiceLoading ? 'Creating Invoice...' : 'Get Payment Address'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Free project: original backing UI
  return (
    <>
      <div className="card sidebar-card backing-sidebar">
        <h3 className="sidebar-title">❤️ Support This Project</h3>
        
        <div className="backing-stats">
          <div className="backer-count-display">
            <span className="backer-count-number">{backerCount}</span>
            <span className="backer-count-label">
              {backerCount === 1 ? 'Backer' : 'Backers'}
            </span>
          </div>
        </div>

        {user ? (
          <div className="backing-actions-sidebar">
            {isBacking ? (
              <>
                <button
                  onClick={handleUnback}
                  className="btn btn-backing-sidebar"
                  title="You're backing this project"
                >
                  ✓ Backing
                </button>
                <button
                  onClick={openBackModal}
                  className="btn btn-secondary-sidebar"
                  title="Update your backing"
                >
                  Edit Backing
                </button>
                
                {userBacking && (
                  <div className="current-backing-info">
                    <div className="current-tier">
                      <span className={`tier-badge tier-${userBacking.tier}`}>
                        {userBacking.tier}
                      </span>
                    </div>
                    {userBacking.message && (
                      <p className="current-message">"{userBacking.message}"</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={openBackModal}
                className="btn btn-primary-sidebar"
              >
                ❤️ Back This Project
              </button>
            )}
          </div>
        ) : (
          <a href="/login" className="btn btn-primary-sidebar">
            Login to Back
          </a>
        )}

        <p className="backing-help-text">
          Show your support and join {backerCount} other{backerCount !== 1 ? 's' : ''} backing this project
        </p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isBacking ? 'Update Your Backing' : 'Back This Project'}</h3>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Support Tier</label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="form-control"
                >
                  <option value="supporter">Supporter</option>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message (Optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Leave a message of support..."
                  className="form-control"
                  rows="3"
                  maxLength="500"
                />
                <small className="text-muted">
                  {message.length}/500 characters
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  <span>Show my support publicly</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBackProject}
              >
                {isBacking ? 'Update Backing' : 'Back Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProjectBackingSidebar;
